import { moneyText } from './agentCasesFormat';

// The wording and small rules of the agent Cases lists, in one place (rows, chips, days, dates).

export const LIST_TILES = ['attention', 'open', 'matched', 'done'];
export const LIST_PATH = { attention: '/cases/attention', open: '/cases/open', matched: '/cases/matched', done: '/cases/done' };
export const CASE_PATH = (id) => `/cases/${id}`;

const EN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// The tabs of each list, in Leo's order, as the server sends them (server/agent_cases.js TABS). Used to draw the pills (without a count)
// while the counts load or if they cannot be read; the counts and the "computed" flags always come from the server.
export const TILE_TABS = {
  attention: ['all', 'price', 'quantity', 'charge_not_ordered', 'missing_line', 'duplicate', 'documents_late', 'order_missing'],
  open: ['all', 'not_confirmed'],
  matched: ['all', 'internal_po'],
  done: [],
};

// The tabs to draw: "All" always, the tab you are on always, and every other tab only when it has something in it. counts = the server's [{ key, count }] for this
// tile (null or empty = not known: draw every tab).
export function visibleTabs(keys, counts, active) {
  if (!Array.isArray(counts) || counts.length === 0) return keys;
  const n = new Map(counts.map((c) => [c.key, typeof c.count === 'number' ? c.count : null]));
  return keys.filter((k) => k === 'all' || k === active || !n.has(k) || n.get(k) === null || n.get(k) > 0);
}

// "18 Sep" / "18 set": a calendar day, shown as the day it is (never moved by the time zone of the browser).
export function dayMonth(iso, lang) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return null;
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  // English months are fixed three letters ("3 Sep", as in Leo's boards; the browser's own short form is "Sept"); Italian uses the browser's.
  if (lang !== 'it') return `${d.getUTCDate()} ${EN_MONTHS[d.getUTCMonth()]}`;
  return new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(d).replace(/\.$/, '');
}

// "14 Jan 2025" / "14 gen 2025": a calendar day with its year, as the Archive board shows it.
export function dayMonthYear(iso, lang) {
  const dm = dayMonth(iso, lang);
  return dm ? `${dm} ${iso.slice(0, 4)}` : null;
}

// A problem makes the Invoice chip red on these tabs (Leo marks the disagreeing document red; we do not store which one, so it is the invoice).
const INVOICE_RED = new Set(['price', 'quantity', 'billed_over_delivered', 'charge_not_ordered', 'missing_line', 'duplicate', 'vat_error']);

// The six document chips, in Leo's order and names (B14): Order, Confirmation, Packing list, Invoice, Delivery note, Paid. ok = on the case, bad = on the case and in disagreement (the
// invoice of a Needs attention row), miss = not on the case. Paid is never recorded, so it is always empty.
export function chipStates(r) {
  const c = r.chips || {};
  return [
    { key: 'order', state: c.order ? 'ok' : 'miss' },
    { key: 'confirmation', state: c.confirmation ? 'ok' : 'miss' },
    { key: 'packing_list', state: c.packing_list ? 'ok' : 'miss' },
    { key: 'invoice', state: c.invoice ? (INVOICE_RED.has(r.reason) ? 'bad' : 'ok') : 'miss' },
    { key: 'delivery', state: c.delivery ? 'ok' : 'miss' },
    // for an agent an invoice counts as paid when its payment term has expired: green "Paid" after, grey "Due <date>" before
    { key: 'paid', state: c.paid ? 'ok' : 'miss', due: c.paid ? null : (r.due_day || null) },
  ];
}

// "Mobus Fabrics → Architex International" (the buyer as written on the invoice, display only); just the supplier when there is none.
export const companiesText = (r) => [r.supplier && r.supplier.name, r.buyer].filter(Boolean).join(' → ');

// "PO 198664" for the case's order; an invoice with no order shows its number.
export function orderText(t, r) {
  if (r.po_number) return t('search.po', { n: r.po_number });
  if (r.reason === 'order_missing' && r.cited_order) return t('search.po', { n: r.cited_order });
  if (r.invoice_number) return r.internal_po ? t('cases.list.internalPo', { n: r.invoice_number }) : t('search.invoiceRef', { n: r.invoice_number });
  return '';
}

// The description line under a Needs attention row: the sentence the case already carries, or the fixed wording for the rows that have none.
export function describeRow(t, lang, r) {
  if (r.reason === 'weak_match') return t('cases.list.weak');
  if (r.reason === 'documents_late') {
    const d = r.since_days == null ? 0 : r.since_days;
    const w = Math.floor(d / 7);
    if (w >= 1) return w === 1 ? t('cases.list.late.week') : t('cases.list.late.weeks', { n: w });
    return d === 1 ? t('cases.list.late.day') : t('cases.list.late.days', { n: d });
  }
  if (r.reason === 'order_missing') {
    if (!r.cited_order) return t('cases.list.om.noRef');
    const date = dayMonth(r.invoice_date, lang);
    return r.invoice_number
      ? (date ? t('cases.list.om.named', { n: r.invoice_number, date }) : t('cases.list.om.namedNoDate', { n: r.invoice_number }))
      : t('cases.list.om.noInvoice');
  }
  if (r.reason === 'documents_overdue') {
    const c = r.chips || {};
    if (r.waiting_days == null) return t('cases.list.reason.other');
    // The server says what is missing (waiting_for): the invoice, or the delivery note when the company requires one.
    if (r.waiting_for === 'invoice') return c.delivery ? t('cases.nt.od.delivery', { n: r.waiting_days }) : t('cases.nt.od.noInvoice', { n: r.waiting_days });
    if (r.waiting_for === 'delivery') return t('cases.nt.od.invoice', { n: r.waiting_days });
    return t('cases.nt.od.generic', { n: r.waiting_days });
  }
  if (r.reason === 'duplicate') return r.invoice_number ? t('cases.list.dup', { n: r.invoice_number }) : (r.summary || t('cases.list.dup.generic'));
  return r.summary || t(`cases.list.reason.${r.reason || 'other'}`);
}

// The amount at the right of a Needs attention row (LEO-FEEDBACK E37): a difference is "+$1,240"; Duplicate and Order missing rows show the
// invoice amount without a "+"; a quantity-only difference is "N units"; weak matches and rows with no stored difference show nothing.
export function amountText(t, r, currency) {
  if (r.reason === 'weak_match') return null;
  const money = (n) => moneyText(n, r.currency || currency);
  if (r.amount != null && Number(r.amount) > 0) return r.amount_kind === 'invoice' ? money(r.amount) : `+${money(r.amount)}`;
  if (r.quantity != null && Number(r.quantity) > 0) return t('cases.list.units', { n: Number(r.quantity).toLocaleString('en-GB') });
  return null;
}

export const daysOpenText = (t, n) => (n == null ? '' : n === 1 ? t('cases.list.daysOpen.one') : t('cases.list.daysOpen.many', { n }));
export const daysText = (t, n) => (n == null ? '—' : n === 1 ? t('cases.list.days.one') : t('cases.list.days.many', { n }));

// Open / Matched "Waiting for": what is missing, or a dash when nothing is.
export function waitingText(t, tile, r) {
  if (tile === 'matched') return r.waiting_for === 'delivery' ? t('cases.list.waiting.deliveryNote') : '—';
  if (r.waiting_for === 'invoice') return t('cases.list.waiting.invoice');
  if (r.waiting_for === 'delivery') return t('cases.list.waiting.delivery');
  return '—';
}

// Matched "Due": days until the invoice is due; a dash when the due date would only be the 60-day assumption.
export function dueText(t, n) {
  if (n == null) return '—';
  if (n === 0) return t('cases.list.due.today');
  return n > 0 ? t('cases.list.due.in', { n }) : t('cases.list.due.late', { n: -n });
}
